import {
    HeartIcon,
    SparklesIcon,
    DocumentMagnifyingGlassIcon,
    ChartPieIcon,
    ShieldCheckIcon,
    LinkIcon
} from '@heroicons/react/24/outline';

const features = [
    {
        name: 'Copilot Readiness',
        description: 'Ensure your SharePoint content is structured perfectly so Microsoft 365 Copilot can accurately index and summarize your data.',
        icon: SparklesIcon,
    },
    {
        name: 'Semantic Structure Analysis',
        description: 'Deep scanning of H1/H2 hierarchies, Aria roles, lists, and tables to ensure your pages meet strict accessibility and SEO standards.',
        icon: DocumentMagnifyingGlassIcon,
    },
    {
        name: 'Readability Scoring',
        description: 'Calculates Flesch Reading Ease to guarantee your corporate communications are clear, coherent, and jargon-free.',
        icon: HeartIcon,
    },
    {
        name: 'Actionable Dashboards',
        description: 'Get a bird\'s-eye view of your entire intranet health, or drill down into the Expert Inspector for itemized penalties.',
        icon: ChartPieIcon,
    },
];

const steps = [
    {
        id: '01',
        name: 'Authenticate & Connect',
        description: 'Sign in securely using Microsoft Entra ID. The platform requires read-only access to scan your configured SharePoint sites.',
    },
    {
        id: '02',
        name: 'Automated Deep Scan',
        description: 'Our engine crawls your SharePoint pages, analyzing raw HTML, ARIA tags, text complexity, and missing metadata.',
    },
    {
        id: '03',
        name: 'Review the Matrix',
        description: 'Pages are plotted on the AI Readiness Matrix. Identify quick wins and prioritize pages that need structural overhauls.',
    },
    {
        id: '04',
        name: 'Optimize & Re-compute',
        description: 'Fix the highlighted issues in SharePoint, then hit Re-compute in our inspector to see your health score instantly soar.',
    },
];

export default function PublicPages() {
    return (
        <div className="bg-white">
            {/* Hero Section */}
            <div className="relative isolate px-6 pt-14 lg:px-8 bg-gray-50 border-b border-gray-100 pb-20">
                <div className="mx-auto max-w-4xl text-center">
                    <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                        Intranet Health & <span className="text-[#2ba6bb]">Copilot Readiness</span>
                    </h1>
                    <p className="mt-6 text-lg leading-8 text-gray-600">
                        A specialized diagnostic platform designed to bridge the gap between human readability and artificial intelligence.
                        We ensure your SharePoint Modern environment is perfectly tuned for Microsoft 365 Copilot.
                    </p>
                </div>
            </div>

            {/* Features Section (What is it?) */}
            <div className="py-24 sm:py-32">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl lg:text-center">
                        <h2 className="text-base font-semibold leading-7 text-[#2ba6bb]">Platform Capabilities</h2>
                        <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                            What exactly do we analyze?
                        </p>
                        <p className="mt-6 text-lg leading-8 text-gray-600">
                            Our proprietary engine looks past the visual layout and reads the underlying semantic truth of your SharePoint pages.
                        </p>
                    </div>
                    <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
                        <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-4">
                            {features.map((feature) => (
                                <div key={feature.name} className="flex flex-col">
                                    <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                                        <feature.icon className="h-5 w-5 flex-none text-[#2ba6bb]" aria-hidden="true" />
                                        {feature.name}
                                    </dt>
                                    <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                                        <p className="flex-auto text-sm">{feature.description}</p>
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </div>
            </div>

            {/* How it works Section */}
            <div className="bg-[#f8fcfd] py-24 sm:py-32 border-y border-[#2ba6bb]/10">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl lg:mx-0">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">How the Platform Works</h2>
                        <p className="mt-6 text-lg leading-8 text-gray-600">
                            A seamless, read-only diagnostic flow that respects your data privacy while delivering deep structural insights.
                        </p>
                    </div>
                    <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
                        <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-4">
                            {steps.map((step) => (
                                <div key={step.name} className="relative pl-12 border-l-2 border-[#2ba6bb]/20 pb-4">
                                    <dt className="text-base font-semibold leading-7 text-gray-900">
                                        <div className="absolute left-[-17px] top-[-4px] flex h-8 w-8 items-center justify-center rounded-full bg-[#2ba6bb] shadow-sm shadow-[#2ba6bb]/50">
                                            <span className="text-xs font-bold text-white">{step.id}</span>
                                        </div>
                                        {step.name}
                                    </dt>
                                    <dd className="mt-3 text-sm leading-6 text-gray-600 text-balance">{step.description}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </div>
            </div>

            {/* Security Banner */}
            <div className="py-16">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-6 rounded-2xl bg-white p-8 ring-1 ring-gray-200 shadow-sm">
                        <div className="flex items-center gap-4">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                                <ShieldCheckIcon className="h-6 w-6" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900">Enterprise Security</h3>
                                <p className="text-sm text-gray-500">Read-only Microsoft Graph access. We do not store your intranet content.</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                                <LinkIcon className="h-6 w-6" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900">Seamless Integration</h3>
                                <p className="text-sm text-gray-500">SSO via Entra ID means zero new passwords to manage.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA */}
            <div className="mx-auto max-w-7xl px-6 pb-24 lg:px-8 pt-8">
                <div className="rounded-3xl bg-[#2ba6bb] px-6 py-12 text-center shadow-xl sm:px-12 sm:py-16">
                    <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                        Ready to audit your tenant?
                    </h2>
                    <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-white/90 pb-8">
                        Sign in with your Microsoft account to instantly access your organization's dashboard and start analyzing your pages.
                    </p>
                </div>
            </div>
        </div>
    );
}
